import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { NavLink, useNavigate, useParams } from "react-router";
import t from "@/lang/lang";
import { H3 } from "@/shared/components/typography";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import {
  addressListLabel,
  oneLine,
} from "@/services/backend/addresses/types/address";
import {
  BulkUpdateAddressRequest,
  useBuildingsByAddressBulkUpdateMutation,
  useBuildingsByAddressShowQuery,
} from "@/services/backend/buildings/api/building-by-address-api";
import { type ByAddress } from "@/services/backend/buildings/types/by-address";
import { AddBuildingComponent } from "@/services/backend/buildings/components/add-building-component";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { is404 } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { BuildingAddressCard } from "@/services/backend/addresses/components/building-address-card";

export function BuildingAddressRoute() {
  const { customerId, addressKey: currentAddressKey } = useParams();
  const {
    data: group,
    isLoading,
    error,
  } = useBuildingsByAddressShowQuery(
    {
      customer: { active: true, values: [customerId!] },
      addressKey: currentAddressKey!,
    },
    {
      skip: !customerId || !currentAddressKey,
      refetchOnMountOrArgChange: true,
    },
  );

  // An address nobody is at is not a failure: no building carries the key, so
  // the backend answers 404 and the page says so in its own words.
  const notFound = is404(error);

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/buildings`,
              label: t("Gebäudeverwaltung"),
            },
            {
              to: `/customers/${customerId}/buildings/addresses`,
              label: t("Adressen"),
            },
            {
              to: `/customers/${customerId}/buildings/addresses/${currentAddressKey}`,
              label: group
                ? oneLine(group.address) || t("Adresse")
                : t("Adresse"),
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-4">
        <PersistenceContextProvider>
          {error && !notFound && <RTKQueryErrorAlert error={error} />}
          {isLoading && <div>{t("Lade Adressen...")}</div>}
          {!isLoading && notFound && (
            <Card>
              <CardContent className="text-muted-foreground py-6 text-sm">
                {t("Diese Adresse wurde nicht gefunden.")}
              </CardContent>
            </Card>
          )}
          {group && customerId && (
            <AddressGroupData customerId={customerId} group={group} />
          )}
        </PersistenceContextProvider>
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function AddressGroupData({
  customerId,
  group,
}: {
  customerId: string;
  group: ByAddress;
}) {
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  // The bulk update somehow motivates to not use
  // debounce here, especially, if there are
  // many buildings and the update may take a while.
  // Feel free to change if needed.
  const [request, setRequest] = useState<BulkUpdateAddressRequest>({
    buildingIds: group.buildings.map((b) => b.id),
    address: group.address,
  });
  const mutation = useBuildingsByAddressBulkUpdateMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      buildingIds: group.buildings.map((b) => b.id),
      address: group.address,
    },
    { delay: 1000 }, // slow down debounce
  );
  // The id change after mutation as well motivates not to use debounce
  useEffect(() => {
    const [, { data }] = mutation;
    if (data) {
      const nextKey = data.addressKey;
      navigate(`/customers/${customerId}/buildings/addresses/${nextKey}`, {
        replace: true,
      });
    }
  });

  const pagedBuildings = useMemo(
    () => group.buildings.slice(offset, offset + limit),
    [group.buildings, limit, offset],
  );

  return (
    <>
      <div className="grid gap-1">
        <H3>{addressListLabel(request.address) || oneLine(request.address)}</H3>
        <div className="text-muted-foreground text-sm">
          {t(
            "Alle Gebäude mit dieser Adresse werden hier gemeinsam bearbeitet.",
          )}
        </div>
      </div>
      <PersistenceStateUsingContext className="ml-auto inline-flex" />
      <BuildingAddressCard
        buildingAddress={request.address}
        onBuildingAddressChange={(address) =>
          setRequest({ ...request, address })
        }
        title={t("Adresse bearbeiten")}
        description={t(
          "Änderungen werden automatisch auf alle Gebäude mit dieser Adresse übertragen.",
        )}
      />
      <div className="flex justify-end">
        <AddBuildingComponent
          customerId={customerId}
          initialAddress={request.address}
          triggerLabel={t("Gebäude mit dieser Adresse")}
        />
      </div>
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Gebäude")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedBuildings.length === 0 && <TableEmptyRow colSpan={4} />}
            {pagedBuildings.map((building) => (
              <TableRow key={building.id}>
                <TableCell className="py-0">
                  <NavLink
                    to={`/customers/${customerId}/buildings/${building.id}`}
                  >
                    <span className="text-primary inline-flex min-h-9 items-center text-sm font-medium">
                      {building.name}
                    </span>
                  </NavLink>
                </TableCell>
                <TableCell>{formatDateTime(building.createdAt)}</TableCell>
                <TableCell>{formatDateTime(building.updatedAt)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" asChild>
                    <NavLink
                      to={`/customers/${customerId}/buildings/${building.id}`}
                    >
                      <Search className="h-4 w-4" />
                    </NavLink>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {group.buildings.length > 0 && (
        <PaginationLimitOffset
          offset={offset}
          limit={limit}
          totalCount={group.buildings.length}
          setLimitOffset={(nextLimit, nextOffset) => {
            setLimit(nextLimit);
            setOffset(nextOffset);
          }}
          className="ml-auto"
        />
      )}
    </>
  );
}
